import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { RequestQueryKeysE } from 'declarations/enums/react-query';
import type { ApiErrorI } from 'declarations/interfaces/errors';
import type { NoopT } from 'declarations/types/common';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import { useDeleteTicket } from 'hooks/api/useTickets';
import { updateSnackbarNotification } from 'reduxStore/features/notifications/notificationsSlice';
import { useAppDispatch } from 'reduxStore/hooks';

import useTicketDetailsPopup from '../../../hooks/useTicketDetailsPopup';

type DeleteTicketParamsT = {
  ticketId: string;
  onClose: NoopT;
};

type UseDeleteTicketPopupT = {
  isDeletingTicket: boolean;
  handleCloseDeletePopup: (params: DeleteTicketParamsT) => NoopT;
  handleClosePopup: (onClosePopupCb: NoopT) => NoopT;
};

const useDeleteTicketPopup = (): UseDeleteTicketPopupT => {
  /**
   * Hooks
   */
  const queryClient = useQueryClient();
  const { handleCloseTicketDetailsPopup } = useTicketDetailsPopup();
  const dispatch = useAppDispatch();

  /**
   * Api hooks
   */
  const deleteTicketMutation = useDeleteTicket({
    onSuccess: () => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
    },
  });

  /**
   * Handlers
   */
  const handleClosePopup = useCallback(
    (onClosePopupCb: NoopT) => () => {
      onClosePopupCb();
    },
    []
  );
  const handleCloseDeletePopup = useCallback(
    (params: DeleteTicketParamsT) => () => {
      deleteTicketMutation.mutate(params, {
        onSuccess: () => {
          dispatch(
            updateSnackbarNotification({
              snackbarMessage: 'Ticket successfully deleted',
              snackBarSeverity: 'success',
            })
          );
          params.onClose();
          handleCloseTicketDetailsPopup();
        },
        onError: error => {
          const errorMsg =
            getErrorMessage(error as ApiErrorI) || getValidationErrorMessage(error as ApiErrorI);
          dispatch(
            updateSnackbarNotification({
              snackbarMessage: `Something went wrong. ${errorMsg || ''}`,
              snackBarSeverity: 'error',
            })
          );
          params.onClose();
        },
      });
    },
    [deleteTicketMutation, dispatch, handleCloseTicketDetailsPopup]
  );

  return {
    isDeletingTicket: deleteTicketMutation.isLoading,
    handleCloseDeletePopup,
    handleClosePopup,
  };
};

export default useDeleteTicketPopup;
